import { ReactElement, useState } from 'react';

import styled from 'styled-components';

import { useAriaId } from '@components/component-helpers';
import { FormError } from '@components/form-error';
import { IconProps } from '@components/icons';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { Tooltip } from '@components/tooltip';
import { useEvent } from '@hooks/use-event';
import { Font, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { AnimatedCheckbox } from './animated-checkbox';
import { OptionIconWrapper, OptionLabelWrapper } from './form-option';

import { VisuallyHidden } from '../visually-hidden';

export type CheckboxState = boolean | 'mixed';

export type CheckboxProps = {
  label: string | JSX.Element;
  labelVisible?: boolean;
  labelFont?: Font;
  tabIndex?: number;
  onChange(value: boolean): void;
  error?: string;
  defaultValue?: boolean;
  value?: CheckboxState;
  isDisabled?: boolean;
  tooltip?: string;
  dataCy?: string;
  icon?: ReactElement<IconProps>;
};

/**
 * Dual- and Tri-state checkbox. For Tri-state, use the controlled version.
 */
export const Checkbox = ({
  defaultValue = false,
  dataCy,
  error,
  icon,
  label,
  labelFont = 'regular14',
  labelVisible = true,
  onChange,
  tabIndex,
  value: userValue,
  isDisabled = false,
  tooltip,
}: CheckboxProps) => {
  const id = useAriaId('checkbox');
  const [selected, setSelected] = useState<CheckboxState>(defaultValue);
  const value = userValue ?? selected;

  const handleClick = useEvent(() => {
    setSelected(!value);
    onChange(!value);
  });

  let labelElement = (
    <OptionLabelWrapper>
      <Text
        tag="label"
        htmlFor={id}
        font={labelFont}
        color={isDisabled ? 'textSecondary' : 'textPrimary'}
        text={label}
      />
    </OptionLabelWrapper>
  );

  if (!labelVisible) {
    labelElement = <VisuallyHidden>{labelElement}</VisuallyHidden>;
  }

  return (
    <Tooltip content={tooltip} placement="left">
      <Container $isDisabled={isDisabled} $isActive={value !== false}>
        <AnimatedCheckbox isDisabled={isDisabled} checked={value} aria-hidden="true" />
        <Input
          id={id}
          data-cy={dataCy}
          type="checkbox"
          aria-checked={value}
          tabIndex={tabIndex === undefined ? 0 : tabIndex}
          onClick={handleClick}
          disabled={isDisabled}
          $isDisabled={isDisabled}
        />
        {icon && (
          <OptionIconWrapper>
            <icon.type {...icon.props} size={16} />
          </OptionIconWrapper>
        )}
        {labelElement}
        <FormError text={error} />
      </Container>
    </Tooltip>
  );
};

const Input = styled.input<{ $isDisabled: boolean }>((p) => ({
  opacity: 0,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  position: 'absolute',
  height: '100%',
  width: '100%',
  cursor: p.$isDisabled ? 'not-allowed' : 'pointer',
}));

const Container = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'start',
  space: 2,
})<{ $isDisabled: boolean; $isActive: boolean }>((p) => ({
  position: 'relative',
  display: 'inline-flex',
  padding: pxToRem(8),

  ...(!p.$isDisabled && {
    '&:has(:focus-visible)': {
      svg: {
        borderRadius: pxToRem(4),
        boxShadow: theme.shadow.focus,
        '--shadow-focus-width': pxToRem(2),
        '--shadow-focus-gap': 0,
        '--shadow-focus-color': theme.color.elementBlue100,
      },
    },

    ...(!p.$isActive && {
      '&:hover': {
        path: {
          fill: theme.color.purple80,
        },
      },
    }),
  }),
}));
